/**
 * The shell every account page sits in — one narrow column, a title, and a line
 * of small print underneath pointing at the other page you might have wanted.
 *
 * Shared so the six pages cannot drift into six slightly different layouts, and
 * so the error region is announced the same way on all of them.
 */

import type { ReactNode } from "react";

interface Props {
  title: string;
  /** Optional line under the title, explaining what the form is for. */
  intro?: ReactNode;
  /** Shown in an alert region above the form when something went wrong. */
  error?: string | null;
  children: ReactNode;
  /** Small print at the bottom — the link to the page they actually wanted. */
  footer?: ReactNode;
  /**
   * What sits above the title. Defaults to the circuit mark, which is right for
   * the five pages a stranger arrives on: a crest over a single decision.
   *
   * `/account` is not one of those — it stacks two of these cards behind a login
   * the visitor has already made, and two identical marks down one narrow column
   * read as a rendering fault rather than as branding. It passes `null` for the
   * card that carries a strip of its own instead, and `null` again for the
   * password form. `null` rather than a `mark` flag so that "something else goes
   * here" is expressible without a second prop that contradicts the first.
   */
  media?: ReactNode;
}

/* Decorative: the title underneath, and the header wordmark above, already name
   the product. Hoisted to a constant so it is the literal default rather than
   something the component branches to build. */
const MARK = <img className="auth__mark" src="/bear/mark.webp" alt="" />;

export function AuthCard({ title, intro, error, children, footer, media = MARK }: Props) {
  return (
    <section className="auth">
      {media}
      <h1 className="auth__title">{title}</h1>
      {intro && <p className="auth__intro muted">{intro}</p>}

      {/* `role="alert"` so a screen reader hears the failure rather than
          leaving someone waiting on a form that silently refused them. */}
      {error && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}

      {children}

      {footer && <p className="auth__footer muted">{footer}</p>}
    </section>
  );
}

/**
 * The complaint about the first empty field, or `null` when all are filled.
 *
 * Every form here sets `noValidate`, deliberately: the browser's own bubbles
 * cannot be styled, do not use the alert region screen readers are already
 * watching, and appear in a different place on every engine. The cost is that
 * `required` on the inputs stops doing anything, so the check has to be ours —
 * without it a submit on an untouched form sends empty strings to the API and
 * spends one of the five attempts a minute the rate limiter allows.
 */
export function firstMissing(fields: { label: string; value: string }[]): string | null {
  const missing = fields.find((field) => !field.value.trim());
  return missing ? `${missing.label} is required.` : null;
}

/** One labelled input. The label is bound with `htmlFor`, which is what lets
 * both a screen reader and `getByLabelText` find the field. */
export function Field({
  id,
  label,
  type,
  value,
  autoComplete,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  type: "email" | "password";
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        required
        onChange={(event) => onChange(event.target.value)}
        {...(hint ? { "aria-describedby": `${id}-hint` } : {})}
      />
      {hint && (
        <span id={`${id}-hint`} className="field__hint muted">
          {hint}
        </span>
      )}
    </div>
  );
}
